import { Button, ButtonMode, Modal, ModalWidth } from '@creditsights/styleguide-web';
import React, { useEffect, useState } from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import classNames from 'classnames/bind';
import { useGlobalModalContext } from '../../../components/GlobalModal';
import useModalErrorHandler from '../../../hooks/modal/useModalErrorHandler';
import { useToast } from '../../../hooks/toast/useToast';
import { useCreateWatchList } from '../../../hooks/watchLists';
import { NotificationMode } from '../../../models/Preference';
import { WatchList } from '../../../models/WatchList';
import { generateNextDefaultName } from '../../../utils/generateNextDefaultName';
import { DEFAULT_WATCHLIST_NAME } from '../../MyContent/WatchlistsTab/WatchlistTab.constants';

import styles from './CompanyCreateNewWatchlistModal.module.scss';
import { GENERIC_ERROR_TEXT } from '../../../Base.constants';
import { removeSubsequentSpaces } from '../../../utils/inputFormatUtils';

const cx = classNames.bind(styles);

interface CompanyCreateNewWatchlistModalProps {
  watchlists: WatchList[];
  issuerTag: string;
  setOpen: (isOpen: boolean) => void;
  setCompanyIsInAnyWatchlist: (isInWatchlist: boolean) => void;
}
type WatchlistNameForm = {
  name: string;
};

const CompanyCreateNewWatchlistModal = ({
  watchlists,
  issuerTag,
  setOpen,
  setCompanyIsInAnyWatchlist,
}: CompanyCreateNewWatchlistModalProps): React.ReactElement => {
  const { mutate: createWatchlist, isLoading: isCreatingWatchlist } = useCreateWatchList();
  const [hasValue, setHasValue] = useState<boolean>(true);
  const { closeModal, setError, store } = useGlobalModalContext();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isModalCloseable = !isCreatingWatchlist || !isLoading;
  useModalErrorHandler(setIsLoading);

  const generatedName = generateNextDefaultName(
    watchlists.map((watchlist) => watchlist.name),
    DEFAULT_WATCHLIST_NAME
  );

  const watchlistNames = watchlists.map((watchlist) => watchlist.name);

  const { register, handleSubmit, setFocus, formState, setValue } = useForm<WatchlistNameForm>({
    mode: 'onChange',
    defaultValues: {
      name: generatedName,
    },
  });
  const { isValid, errors } = formState;

  useEffect(() => {
    setFocus('name');
  }, []);

  const handleChange = (e) => {
    if (e.target.value.trim().length === 0) {
      setHasValue(false);
    } else {
      setHasValue(true);
    }
    setValue('name', removeSubsequentSpaces(e.target.value));
  };

  const renderFooter = () => (
    <>
      <Button
        dataTestId="company-create-new-watchlist-cancel-button"
        disabled={isCreatingWatchlist}
        mode={ButtonMode.SECONDARY}
        onClick={closeModal}
        className={cx('cancel-button')}
      >
        Cancel
      </Button>
      <Button
        dataTestId="company-create-new-watchlist-submit-button"
        type="submit"
        onClick={handleSubmit(onSubmit)}
        disabled={!isValid || !hasValue || isCreatingWatchlist || isLoading}
        loading={isCreatingWatchlist}
        mode={ButtonMode.PRIMARY}
      >
        Save
      </Button>
    </>
  );

  const isUnique: RegisterOptions['validate'] = (name: string) => {
    const trimmedName = name.trim();
    return watchlistNames?.includes(trimmedName) ? `${trimmedName} already exists` : true;
  };

  const onSubmit = ({ name }) => {
    if (!hasValue || isCreatingWatchlist) {
      return null;
    }
    setIsLoading(true);
    setError('');
    const trimmedName = name.trim();
    return onNewWatchlistCreate(trimmedName);
  };

  const onNewWatchlistCreate = (watchlistName: string) => {
    createWatchlist(
      {
        name: watchlistName,
        myCS: true,
        tags: [issuerTag],
        mode: NotificationMode.Instant,
      },
      {
        onSuccess: () => {
          showToast(
            <span>
              Watchlist <strong>{watchlistName}</strong> has been created, you can now add issuers to it
            </span>
          );
          closeModal();
          setOpen(false);
          setCompanyIsInAnyWatchlist(true);
        },
        onError: () => {
          setError(GENERIC_ERROR_TEXT);
        },
      }
    );
  };

  return (
    <Modal
      headerTitle="New Watchlist"
      onClose={closeModal}
      open
      footer={renderFooter()}
      width={ModalWidth.Small}
      closeOnEscape={isModalCloseable}
      closeOnDimmerClick={isModalCloseable}
      dataTestId="company-add-to-watchlist-modal"
      className={cx('body')}
      error={store.props.error}
      closeOnButtonClick={isModalCloseable}
    >
      <form className={cx('form')} onSubmit={handleSubmit(onSubmit)}>
        <label className={cx('input-wrapper')} htmlFor="name">
          <span className={cx('label')}>Watchlist Name</span>
          <input
            autoComplete="off"
            id="name"
            className={cx('input', {
              'is-error': !isValid,
            })}
            data-testid="create-new-watchlist-modal-watchlist-name"
            placeholder="Provide a name for your watchlist"
            type="text"
            {...register('name', {
              onChange: handleChange,
              validate: {
                isUnique,
              },
              maxLength: { value: 200, message: 'Name is too long' },
            })}
          />
        </label>
        {!isValid && (
          <div data-testid="create-new-watchlist-modal-error-field" className={cx('error-field')}>
            {Object.values(errors).map((e) => (
              <p key={e.message}>{e.message}</p>
            ))}
          </div>
        )}
      </form>
    </Modal>
  );
};

export default CompanyCreateNewWatchlistModal;
